import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import { ArrowLeft, Copy, Lock, Trash2, Unlock } from 'lucide-react';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  Input,
  Label,
  Switch,
  Textarea,
} from '@artstorefronts/ui';
import {
  QUALIFY_ON,
  SIGNUP_GATE_MODES,
  fillCopy,
  parseSettings,
  stepsNotAllowedWhileWaiting,
  strategySettingsSchema,
  type QualifyOn,
  type SignupGateMode,
  type StrategySettings,
} from '@arthelper/shared/acquisition/strategy';
import AdminLayout from '../../../components/AdminLayout';
import {
  ACQUISITION_STRATEGIES,
  ACQUISITION_STRATEGY,
  CLONE_STRATEGY,
  CREATE_STRATEGY,
  DELETE_STRATEGY,
  RELEASE_WAITLIST,
  UPDATE_STRATEGY,
  WAITING_FLOW_VERSIONS,
  type Strategy,
  type WaitingFlowVersion,
} from './queries';
import { HeldSummary } from './HeldSummary';
import { PreviewStrategy } from './PreviewStrategy';
import { GATE_LABELS, METRIC_ROWS, StatusBadge, errorMessage, formatDate, label } from './shared';

type CopyKey = keyof StrategySettings['copy'];
type Confirm = 'delete' | 'release' | null;

const COPY_MAX = 280;
const SAMPLE_INVITER = 'Maya Chen';
const INPUT_CLASS = 'h-11 text-base md:text-sm';
const SELECT_CLASS =
  'h-11 w-full rounded-md border border-gray-300 bg-white px-3 text-base disabled:bg-gray-50 disabled:text-gray-500 md:text-sm';

const GATE_HINTS: Record<SignupGateMode, string> = {
  open: 'Anyone can sign up. The waitlist is never shown.',
  refer_to_join: 'Cold signups wait until enough friends they invite join.',
  invite_required: 'Only people with an invite can sign up.',
};

const QUALIFY_LABELS: Record<QualifyOn, string> = {
  signup: 'The friend signs up',
  first_upload: 'The friend uploads their first artwork',
};

/** "1 person" / "3 people" — for the waitlist release counts. */
function people(count: number): string {
  return `${count.toLocaleString()} ${count === 1 ? 'person' : 'people'}`;
}

const COPY_FIELDS: Array<{ key: CopyKey; label: string; multiline: boolean; waiting?: boolean }> = [
  { key: 'waitlistHeadline', label: 'Waitlist headline', multiline: false },
  { key: 'waitlistBody', label: 'Waitlist body', multiline: true },
  { key: 'welcomeHeadline', label: 'Welcome headline', multiline: false },
  { key: 'welcomeBody', label: 'Welcome body', multiline: true },
  { key: 'waitingPrompt', label: 'Waitlist page: build-your-profile prompt', multiline: true, waiting: true },
  { key: 'waitingDoneHeadline', label: 'Waiting wizard: finish headline', multiline: false, waiting: true },
  { key: 'waitingDoneBody', label: 'Waiting wizard: finish body', multiline: true, waiting: true },
];

/** The steps in a version that a waiting wizard can't hold (social, finale, completion). */
function refusedSteps(version: WaitingFlowVersion): string[] {
  return stepsNotAllowedWhileWaiting(version.steps.filter((step) => step.isEnabled).map((step) => step.stepCode));
}

/**
 * Build while you wait: pick the onboarding version held members run. Only profile-only versions
 * can be picked — following, joining and posting wait until they're in, and run then.
 */
function WaitingWizardField({
  value,
  frozen,
  onChange,
}: {
  value: string | null;
  frozen: boolean;
  onChange: (value: string | null) => void;
}) {
  const { data } = useQuery<{ onboardingVersions: WaitingFlowVersion[] }>(WAITING_FLOW_VERSIONS);
  const versions = data?.onboardingVersions ?? [];
  const eligible = versions.filter((version) => refusedSteps(version).length === 0);
  return (
    <div className="space-y-2 sm:col-span-2" data-testid="waiting-wizard-field">
      <div className="flex min-h-11 items-start gap-3">
        <Switch
          id="waiting-wizard"
          checked={value != null}
          disabled={frozen || (value == null && eligible.length === 0)}
          onCheckedChange={(checked) => onChange(checked ? (eligible[0]?.id ?? null) : null)}
        />
        <Label htmlFor="waiting-wizard" className="leading-snug">
          Let them build their profile while they wait
        </Label>
      </div>
      {value != null && (
        <select
          id="waiting-wizard-version"
          aria-label="Onboarding version they run while waiting"
          className={SELECT_CLASS}
          value={value}
          disabled={frozen}
          onChange={(e) => onChange(e.target.value)}
        >
          {versions.map((version) => {
            const refused = refusedSteps(version);
            return (
              <option key={version.id} value={version.id} disabled={refused.length > 0}>
                v{version.version} · {version.name}
                {refused.length > 0 ? ` (has ${refused.length} social step${refused.length === 1 ? '' : 's'})` : ''}
              </option>
            );
          })}
        </select>
      )}
      <p className="text-xs text-gray-500">
        They run this onboarding version from the waitlist page: name, photo, bio, links, their work. It stays private
        until they&apos;re in, and finishes on &ldquo;we&apos;ll let you know&rdquo;. Once let in, they finish the
        default wizard&apos;s remaining steps. Only versions without social, finale or completion steps can be picked
        {eligible.length === 0 ? ' — none exist yet; make one under Onboarding' : ''}.
      </p>
    </div>
  );
}

/** Not built yet: shown so admins know it is coming, but cannot be picked. */
const COMING_SOON: SignupGateMode[] = ['invite_required'];

interface Form {
  name: string;
  description: string;
  settings: StrategySettings;
}

function toForm(strategy: Strategy | null | undefined): Form {
  if (!strategy) {
    return { name: '', description: '', settings: parseSettings({}) };
  }
  return { name: strategy.name, description: strategy.description ?? '', settings: parseSettings(strategy.settings) };
}

function validate(form: Form): string | null {
  if (form.name.trim() === '') {
    return 'Give the strategy a name.';
  }
  const result = strategySettingsSchema.safeParse(form.settings);
  if (result.success) {
    return null;
  }
  return result.error.issues.map((issue) => `${issue.path.join('.')}: ${issue.message}`).join('; ');
}

function saveLabel(saving: boolean, existing: boolean): string {
  if (saving) {
    return 'Saving…';
  }
  return existing ? 'Save draft' : 'Create draft';
}

function PreviewCard({
  title,
  headline,
  body,
  note,
}: {
  title: string;
  headline: string;
  body: string;
  note?: string;
}) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4">
      <div className="text-xs font-medium uppercase tracking-wider text-gray-400">{title}</div>
      <h3 className="mt-2 text-xl font-semibold text-gray-900">{headline}</h3>
      <p className="mt-2 whitespace-pre-wrap text-sm text-gray-600">{body}</p>
      {note && <p className="mt-3 text-xs text-gray-400">{note}</p>}
    </div>
  );
}

function Preview({ settings }: { settings: StrategySettings }) {
  const values = { count: settings.signupGate.requiredReferrals, inviter: SAMPLE_INVITER };
  const gated = settings.signupGate.mode !== 'open';
  return (
    <div className="space-y-4">
      <PreviewCard
        title="Waitlist"
        headline={fillCopy(settings.copy.waitlistHeadline, values)}
        body={fillCopy(settings.copy.waitlistBody, values)}
        note={gated ? undefined : 'Not shown while signup is open.'}
      />
      <PreviewCard
        title="Welcome"
        headline={fillCopy(settings.copy.welcomeHeadline, values)}
        body={fillCopy(settings.copy.welcomeBody, values)}
        note={`{inviter} previews as "${SAMPLE_INVITER}".`}
      />
    </div>
  );
}

function MetricsGrid({ strategy }: { strategy: Strategy }) {
  return (
    <dl className="grid grid-cols-2 gap-3 sm:grid-cols-3">
      {METRIC_ROWS.map((row) => (
        <div key={row.label} className="rounded-md bg-gray-50 p-3" title={row.hint}>
          <dt className="text-xs text-gray-500">{row.label}</dt>
          <dd className="mt-1 text-lg font-semibold tabular-nums text-gray-900">{row.value(strategy.metrics)}</dd>
        </div>
      ))}
    </dl>
  );
}

function AcquisitionStrategyEditor() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [form, setForm] = useState<Form>(() => toForm(null));
  const [formError, setFormError] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<Confirm>(null);

  const { data, loading } = useQuery<{ acquisitionStrategyById: Strategy | null }>(ACQUISITION_STRATEGY, {
    variables: { id },
    skip: !id,
    fetchPolicy: 'cache-and-network',
  });
  const strategy = data?.acquisitionStrategyById ?? null;

  const refetchQueries = [{ query: ACQUISITION_STRATEGIES }];
  const [create, { loading: creating }] = useMutation(CREATE_STRATEGY, { refetchQueries });
  const [update, { loading: updating }] = useMutation(UPDATE_STRATEGY, { refetchQueries });
  const [clone, { loading: cloning }] = useMutation(CLONE_STRATEGY, { refetchQueries });
  const [remove, { loading: removing }] = useMutation(DELETE_STRATEGY, { refetchQueries });
  const [release, { loading: releasing }] = useMutation(RELEASE_WAITLIST, {
    refetchQueries: [...refetchQueries, { query: ACQUISITION_STRATEGY, variables: { id } }],
  });

  useEffect(() => {
    setForm(toForm(strategy));
  }, [strategy]);

  const frozen = Boolean(strategy?.activatedAt);
  const saving = creating || updating;
  const gate = form.settings.signupGate;
  const canDelete = strategy?.status === 'draft' && !frozen;
  const waitlisted = strategy?.metrics.waitlisted ?? 0;
  const canRelease = strategy?.status === 'archived' && gate.mode !== 'open' && waitlisted > 0;

  const setGate = <K extends keyof StrategySettings['signupGate']>(
    key: K,
    value: StrategySettings['signupGate'][K]
  ) => {
    setForm((prev) => ({
      ...prev,
      settings: { ...prev.settings, signupGate: { ...prev.settings.signupGate, [key]: value } },
    }));
  };

  const setCopy = (key: CopyKey, value: string) => {
    setForm((prev) => ({ ...prev, settings: { ...prev.settings, copy: { ...prev.settings.copy, [key]: value } } }));
  };

  const handleSave = async () => {
    const problem = validate(form);
    setFormError(problem);
    if (problem) {
      return;
    }
    const input = { name: form.name.trim(), description: form.description.trim() || null, settings: form.settings };
    try {
      if (!id) {
        const result = await create({ variables: { input } });
        toast.success('Strategy created.');
        navigate(`/admin/acquisition/${result.data.createAcquisitionStrategy.id}`, { replace: true });
        return;
      }
      await update({ variables: { id, input } });
      toast.success('Saved.');
    } catch (err) {
      setFormError(errorMessage(err, 'Could not save.'));
    }
  };

  const handleClone = async () => {
    try {
      const result = await clone({ variables: { id } });
      const copy = result.data.cloneAcquisitionStrategy;
      toast.success(`Cloned to v${copy.version}.`);
      navigate(`/admin/acquisition/${copy.id}`);
    } catch (err) {
      toast.error(errorMessage(err, 'Could not clone.'));
    }
  };

  const handleDelete = async () => {
    try {
      await remove({ variables: { id } });
      toast.success('Draft deleted.');
      navigate('/admin/acquisition');
    } catch (err) {
      toast.error(errorMessage(err, 'Could not delete.'));
    } finally {
      setConfirm(null);
    }
  };

  const handleRelease = async () => {
    try {
      const result = await release({ variables: { id } });
      toast.success(`Released ${people(result.data.releaseAcquisitionWaitlist)}.`);
    } catch (err) {
      toast.error(errorMessage(err, 'Could not release the waitlist.'));
    } finally {
      setConfirm(null);
    }
  };

  if (id && loading && !strategy) {
    return (
      <AdminLayout title="Acquisition strategy" description="Loading…">
        <div className="px-4 text-gray-500 md:px-0">Loading strategy…</div>
      </AdminLayout>
    );
  }

  if (id && !loading && !strategy) {
    return (
      <AdminLayout title="Acquisition strategy" description="Not found">
        <div className="px-4 text-gray-500 md:px-0">No strategy with that id.</div>
      </AdminLayout>
    );
  }

  const title = strategy ? label(strategy) : 'New strategy';
  const description = strategy
    ? `Created ${formatDate(strategy.createdAt)} · activated ${formatDate(strategy.activatedAt)} · deactivated ${formatDate(strategy.deactivatedAt)}`
    : 'A new draft. Nothing changes for visitors until you activate it from the list.';

  return (
    <AdminLayout
      title={title}
      description={description}
      titleExtra={strategy ? <StatusBadge status={strategy.status} /> : undefined}
    >
      <div className="space-y-6 px-4 md:px-0">
        <Link
          to="/admin/acquisition"
          className="inline-flex min-h-11 items-center gap-1 text-sm text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="h-4 w-4" />
          All strategies
        </Link>

        {frozen && (
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4">
            <p className="flex items-center gap-2 text-sm text-amber-900">
              <Lock className="h-4 w-4 shrink-0" aria-hidden />
              This strategy has run, so its settings are frozen. Clone it to change anything.
            </p>
            <Button type="button" className="h-11 gap-1.5 md:h-9" disabled={cloning} onClick={() => void handleClone()}>
              <Copy className="h-3.5 w-3.5" aria-hidden />
              {cloning ? 'Cloning…' : 'Clone to new version'}
            </Button>
          </div>
        )}

        {canRelease && (
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-gray-200 bg-white p-4">
            <p className="text-sm text-gray-700">{people(waitlisted)} still waitlisted under this strategy.</p>
            <Button
              type="button"
              variant="outline"
              className="h-11 gap-1.5 md:h-9"
              disabled={releasing}
              onClick={() => setConfirm('release')}
            >
              <Unlock className="h-3.5 w-3.5" aria-hidden />
              Release {people(waitlisted)}
            </Button>
          </div>
        )}

        {strategy && <PreviewStrategy strategyId={strategy.id} />}

        {strategy && strategy.activatedAt && gate.mode !== 'open' && <HeldSummary strategyId={strategy.id} />}

        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,22rem)]">
          <fieldset disabled={frozen} className="min-w-0 space-y-6">
            <section className="space-y-4 rounded-lg bg-white p-4 shadow md:p-6">
              <div className="space-y-2">
                <Label htmlFor="strategy-name">Name</Label>
                <Input
                  id="strategy-name"
                  className={INPUT_CLASS}
                  maxLength={120}
                  value={form.name}
                  onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="strategy-description">Description</Label>
                <Textarea
                  id="strategy-description"
                  className="text-base md:text-sm"
                  rows={2}
                  maxLength={1000}
                  placeholder="What this version tests and why"
                  value={form.description}
                  onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
                />
              </div>
            </section>

            <section className="space-y-4 rounded-lg bg-white p-4 shadow md:p-6">
              <h2 className="text-lg font-semibold">Signup gate</h2>
              <div role="radiogroup" aria-label="Signup gate mode" className="space-y-2">
                {SIGNUP_GATE_MODES.map((mode) => {
                  const soon = COMING_SOON.includes(mode);
                  return (
                    <label
                      key={mode}
                      className={`flex min-h-11 cursor-pointer items-start gap-3 rounded-md border p-3 ${
                        gate.mode === mode ? 'border-gray-900' : 'border-gray-200'
                      } ${soon ? 'cursor-not-allowed opacity-60' : ''}`}
                    >
                      <input
                        type="radio"
                        name="gate-mode"
                        className="mt-1 h-4 w-4"
                        value={mode}
                        checked={gate.mode === mode}
                        disabled={soon}
                        onChange={() => setGate('mode', mode)}
                      />
                      <span>
                        <span className="text-sm font-medium text-gray-900">{GATE_LABELS[mode]}</span>
                        {soon && (
                          <span className="ml-2 rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
                            Coming soon
                          </span>
                        )}
                        <span className="block text-xs text-gray-500">{GATE_HINTS[mode]}</span>
                      </span>
                    </label>
                  );
                })}
              </div>

              {gate.mode !== 'open' && (
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="required-referrals">Friends needed to get in</Label>
                    <Input
                      id="required-referrals"
                      type="number"
                      inputMode="numeric"
                      min={1}
                      max={50}
                      className={INPUT_CLASS}
                      value={gate.requiredReferrals}
                      onChange={(e) => setGate('requiredReferrals', Number(e.target.value))}
                    />
                    <p className="text-xs text-gray-500">1 to 50. Fills {'{count}'} in the copy.</p>
                  </div>
                  <div className="space-y-2">
                    <div className="flex min-h-11 items-center gap-3">
                      <Switch
                        id="release-valve"
                        checked={gate.releaseAfterDays != null}
                        disabled={frozen}
                        onCheckedChange={(checked) => setGate('releaseAfterDays', checked ? 7 : null)}
                      />
                      <Label htmlFor="release-valve" className="leading-snug">
                        Let them in anyway after a set number of days
                      </Label>
                    </div>
                    {gate.releaseAfterDays != null && (
                      <Input
                        id="release-after-days"
                        type="number"
                        inputMode="numeric"
                        min={1}
                        max={60}
                        aria-label="Days until they get in anyway"
                        className={INPUT_CLASS}
                        value={gate.releaseAfterDays}
                        disabled={frozen}
                        onChange={(e) => setGate('releaseAfterDays', Number(e.target.value))}
                      />
                    )}
                    <p className="text-xs text-gray-500">
                      Off: held until they refer. On (1 to 60 days): the gate and waitlist pages say the date out loud.
                      Today only ~0.1% of new members refer anyone, so test it rather than assume it.
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="qualify-on">A referral counts when</Label>
                    <select
                      id="qualify-on"
                      className={SELECT_CLASS}
                      value={gate.qualifyOn}
                      onChange={(e) => setGate('qualifyOn', e.target.value as QualifyOn)}
                    >
                      {QUALIFY_ON.map((value) => (
                        <option key={value} value={value}>
                          {QUALIFY_LABELS[value]}
                        </option>
                      ))}
                    </select>
                  </div>
                  <WaitingWizardField
                    value={gate.waitingFlowVersionId}
                    frozen={frozen}
                    onChange={(value) => setGate('waitingFlowVersionId', value)}
                  />
                  <div className="flex min-h-11 items-start gap-3 sm:col-span-2">
                    <Switch
                      id="referred-bypass"
                      checked={gate.referredLinkBypasses}
                      disabled={frozen}
                      onCheckedChange={(checked) => setGate('referredLinkBypasses', checked)}
                    />
                    <Label htmlFor="referred-bypass" className="leading-snug">
                      People arriving through a referral or invite link skip the gate
                    </Label>
                  </div>
                </div>
              )}
            </section>

            <section className="space-y-4 rounded-lg bg-white p-4 shadow md:p-6">
              <div>
                <h2 className="text-lg font-semibold">Copy</h2>
                <p className="text-xs text-gray-500">
                  Use {'{count}'} for the number of friends needed and {'{inviter}'} for the person who let them in.
                </p>
              </div>
              {COPY_FIELDS.filter((field) => !field.waiting || gate.waitingFlowVersionId != null).map((field) => {
                const value = form.settings.copy[field.key];
                const inputId = `copy-${field.key}`;
                return (
                  <div key={field.key} className="space-y-2">
                    <div className="flex items-baseline justify-between gap-2">
                      <Label htmlFor={inputId}>{field.label}</Label>
                      <span className={`text-xs ${value.length > COPY_MAX ? 'text-red-600' : 'text-gray-400'}`}>
                        {value.length}/{COPY_MAX}
                      </span>
                    </div>
                    {field.multiline ? (
                      <Textarea
                        id={inputId}
                        rows={3}
                        className="text-base md:text-sm"
                        value={value}
                        onChange={(e) => setCopy(field.key, e.target.value)}
                      />
                    ) : (
                      <Input
                        id={inputId}
                        className={INPUT_CLASS}
                        value={value}
                        onChange={(e) => setCopy(field.key, e.target.value)}
                      />
                    )}
                  </div>
                );
              })}
            </section>
          </fieldset>

          <aside className="min-w-0 space-y-6">
            <section className="space-y-3">
              <h2 className="text-lg font-semibold">Preview</h2>
              <Preview settings={form.settings} />
            </section>
            {strategy && (
              <section className="space-y-3 rounded-lg bg-white p-4 shadow">
                <h2 className="text-lg font-semibold">Results</h2>
                <MetricsGrid strategy={strategy} />
              </section>
            )}
          </aside>
        </div>

        {formError && <p className="text-sm text-red-600">{formError}</p>}

        <div className="flex flex-wrap items-center gap-3">
          {!frozen && (
            <Button
              type="button"
              className="h-11 bg-gray-900 hover:bg-gray-800 md:h-9"
              disabled={saving}
              onClick={() => void handleSave()}
            >
              {saveLabel(saving, Boolean(id))}
            </Button>
          )}
          {canDelete && (
            <Button
              type="button"
              variant="outline"
              className="h-11 gap-1.5 text-red-700 hover:bg-red-50 md:h-9"
              disabled={removing}
              onClick={() => setConfirm('delete')}
            >
              <Trash2 className="h-3.5 w-3.5" aria-hidden />
              Delete draft
            </Button>
          )}
        </div>
      </div>

      <AlertDialog open={confirm !== null} onOpenChange={(open) => !open && setConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirm === 'delete' ? 'Delete this draft?' : `Let ${people(waitlisted)} in?`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirm === 'delete'
                ? 'It has never run, so nothing else changes. This cannot be undone.'
                : 'Everyone still waitlisted under this strategy gets access right away, whether or not their friends joined. This cannot be undone.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="h-11 md:h-9">Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="h-11 md:h-9"
              onClick={() => void (confirm === 'delete' ? handleDelete() : handleRelease())}
            >
              {confirm === 'delete' ? 'Delete' : 'Release'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  );
}

export default AcquisitionStrategyEditor;
